import React from 'react';

/**
 * CallUsageChart — Minimal SVG bar chart for call duration buckets.
 *
 * Props:
 *  - data: Array<{ label: string, value: number, sublabel?: string }>
 *  - height: number (default 140)
 *  - barColor: tailwind class fragment (default 'fill-fuchsia-500')
 *  - yUnit: 's' (seconds → auto formats) | 'min' | 'h' (default 's')
 */
const formatSeconds = (s) => {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s - h * 3600) / 60);
  return m ? `${h}h${m}` : `${h}h`;
};

const CallUsageChart = ({ data = [], height = 140, barColor = 'fill-fuchsia-500', testid }) => {
  const max = Math.max(1, ...data.map(d => d.value || 0));
  const barWidth = 100 / Math.max(1, data.length);

  return (
    <div className="w-full" data-testid={testid || 'call-usage-chart'}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height: `${height}px` }}
        aria-label="Call usage chart"
      >
        {/* Horizontal grid lines */}
        {[0.25, 0.5, 0.75].map((p, i) => (
          <line
            key={i}
            x1="0" x2="100"
            y1={height - height * p}
            y2={height - height * p}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="0.3"
          />
        ))}
        {data.map((d, i) => {
          const h = ((d.value || 0) / max) * (height - 8);
          return (
            <g key={i}>
              <rect
                x={i * barWidth + barWidth * 0.15}
                y={height - h}
                width={barWidth * 0.7}
                height={h}
                className={barColor}
                rx="0.4"
              >
                <title>{`${d.label}: ${formatSeconds(d.value)}${d.sublabel ? ` • ${d.sublabel}` : ''}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>

      {/* X-axis labels — show first, middle, last + total */}
      <div className="flex items-center justify-between text-[10px] text-gray-400 mt-1 px-1">
        <span>{data[0]?.label || ''}</span>
        {data.length > 8 && <span>{data[Math.floor(data.length / 2)]?.label || ''}</span>}
        <span>{data[data.length - 1]?.label || ''}</span>
      </div>
    </div>
  );
};

export { formatSeconds };
export default CallUsageChart;
